import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getUrineTestResults } from '../services/labService';
import { getMemberLabData, getHistory } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';
import UrineTestFormula from '../formula/UrineTest';

const TestResultsContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#4CAF50'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UrineIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const ResultCard = styled.div`
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  background-color: #00BBD3;
  padding: 16px 20px;
  color: white;
  text-align: center;
`;

const HeaderText = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  flex: 1;
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: bold;
  text-align: right;
  flex: 1;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e1e5e9;
  margin: 16px 0;
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #333333;
  margin: 20px 0 12px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const UrineTestResultsPage = () => {
  const navigate = useNavigate();
  const [urineResults, setUrineResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusColor, setStatusColor] = useState('#e0e0e0');

  useEffect(() => {
    const loadUrineResults = async () => {
      try {
        setLoading(true);

        // ลองดึงจาก memberLabData ก่อน
        let memberLabData = null;
        try {
          memberLabData = await getMemberLabData();
          console.log('📊 Member Lab Data (Urine):', memberLabData);
        } catch (memberLabError) {
          console.warn('⚠️ Could not load member lab data:', memberLabError);
        }

        const labData = memberLabData?.labData || {};

        // ดึงประวัติทั้งหมดของแต่ละ parameter
        const urinalysisParams = [
          { key: 'urinalysis_color', label: 'สีของปัสสาวะ' },
          { key: 'urinalysis_appearance', label: 'ความใส' },
          { key: 'urinalysis_ph', label: 'pH' },
          { key: 'urinalysis_specific_gravity', label: 'ความถ่วงจำเพาะ' },
          { key: 'urinalysis_protein', label: 'โปรตีน' },
          { key: 'urinalysis_glucose', label: 'น้ำตาล' },
          { key: 'urinalysis_ketone', label: 'คีโตน' },
          { key: 'urinalysis_blood', label: 'เลือด' },
          { key: 'urinalysis_leukocyte', label: 'เม็ดเลือดขาว' },
          { key: 'urinalysis_nitrite', label: 'ไนเตรต' },
          { key: 'urinalysis_bilirubin', label: 'บิลิรูบิน' },
          { key: 'urinalysis_urobilinogen', label: 'ยูโรบิลินเจน' },
          { key: 'urinalysis_rbc', label: 'เซลล์เม็ดเลือดแดง' },
          { key: 'urinalysis_wbc', label: 'เซลล์เม็ดเลือดขาว' }
        ];

        // รวมข้อมูลจากทุก parameter ตามวันที่
        const visitMap = new Map();

        urinalysisParams.forEach(param => {
          const history = getHistory(labData, param.key);
          history.forEach(item => {
            const date = item.date;
            if (!visitMap.has(date)) {
              visitMap.set(date, {
                date: date,
                hospital: 'โรงพยาบาล',
                results: {}
              });
            }
            visitMap.get(date).results[param.label] = item.value;
            visitMap.get(date)[param.key] = item.value; // เก็บค่าดิบไว้ด้วย
          });
        });

        const formattedResults = Array.from(visitMap.values())
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        console.log('🔬 Formatted Urine Results:', formattedResults);

        if (formattedResults.length > 0) {
          setUrineResults(formattedResults);

          // Calculate status color from latest result
          const latestResult = formattedResults[0];
          const urineData = {
            ph: latestResult.urinalysis_ph,
            color: latestResult.urinalysis_color,
            clarity: latestResult.urinalysis_appearance,
            specificGravity: latestResult.urinalysis_specific_gravity,
            protein: latestResult.urinalysis_protein,
            glucose: latestResult.urinalysis_glucose,
            ketones: latestResult.urinalysis_ketone,
            blood: latestResult.urinalysis_blood,
            bilirubin: latestResult.urinalysis_bilirubin,
            urobilinogen: latestResult.urinalysis_urobilinogen,
            leukocytes: latestResult.urinalysis_leukocyte,
            nitrite: latestResult.urinalysis_nitrite,
            redBloodCells: latestResult.urinalysis_rbc,
            whiteBloodCells: latestResult.urinalysis_wbc
          };

          const formulaResult = UrineTestFormula(urineData);
          const colorMap = {
            'green': '#34A853',
            'red': '#ff4757',
            'gray': '#e0e0e0'
          };
          setStatusColor(colorMap[formulaResult.color] || '#e0e0e0');
        } else {
          // Fallback: ถ้าไม่มีข้อมูลจาก memberLabData ให้ลอง API เดิม
          const response = await getUrineTestResults();
          console.log('Urine Test Response (Fallback):', response);

          if (response?.result && Array.isArray(response.list)) {
            const formattedResults = response.list.map(item => {
              const results = {};

              if (item.urinalysis_color) results['สีของปัสสาวะ'] = item.urinalysis_color;
              if (item.urinalysis_appearance) results['ความใส'] = item.urinalysis_appearance;
              if (item.urinalysis_ph) results['pH'] = item.urinalysis_ph;
              if (item.urinalysis_specific_gravity) results['ความถ่วงจำเพาะ'] = item.urinalysis_specific_gravity;
              if (item.urinalysis_protein) results['โปรตีน'] = item.urinalysis_protein;
              if (item.urinalysis_glucose) results['น้ำตาล'] = item.urinalysis_glucose;
              if (item.urinalysis_ketone) results['คีโตน'] = item.urinalysis_ketone;
              if (item.urinalysis_blood) results['เลือด'] = item.urinalysis_blood;
              if (item.urinalysis_leukocyte) results['เม็ดเลือดขาว'] = item.urinalysis_leukocyte;
              if (item.urinalysis_nitrite) results['ไนเตรต'] = item.urinalysis_nitrite;
              if (item.urinalysis_bilirubin) results['บิลิรูบิน'] = item.urinalysis_bilirubin;
              if (item.urinalysis_urobilinogen) results['ยูโรบิลินเจน'] = item.urinalysis_urobilinogen;
              if (item.urinalysis_rbc) results['เซลล์เม็ดเลือดแดง'] = item.urinalysis_rbc;
              if (item.urinalysis_wbc) results['เซลล์เม็ดเลือดขาว'] = item.urinalysis_wbc;

              return {
                date: item.visit_date || item.dateadd || '-',
                hospital: item.hospital_name || 'โรงพยาบาล',
                results: results,
                rawData: item
              };
            });

            setUrineResults(formattedResults);

            if (formattedResults.length > 0) {
              const latestResult = response.list[0];
              const urineData = {
                ph: latestResult.urinalysis_ph,
                color: latestResult.urinalysis_color,
                clarity: latestResult.urinalysis_appearance,
                specificGravity: latestResult.urinalysis_specific_gravity,
                protein: latestResult.urinalysis_protein,
                glucose: latestResult.urinalysis_glucose,
                ketones: latestResult.urinalysis_ketone,
                blood: latestResult.urinalysis_blood,
                bilirubin: latestResult.urinalysis_bilirubin,
                urobilinogen: latestResult.urinalysis_urobilinogen,
                leukocytes: latestResult.urinalysis_leukocyte,
                nitrite: latestResult.urinalysis_nitrite,
                redBloodCells: latestResult.urinalysis_rbc,
                whiteBloodCells: latestResult.urinalysis_wbc
              };

              const formulaResult = UrineTestFormula(urineData);
              const colorMap = {
                'green': '#34A853',
                'red': '#ff4757',
                'gray': '#e0e0e0'
              };
              setStatusColor(colorMap[formulaResult.color] || '#e0e0e0');
            }
          }
        }
      } catch (error) {
        console.error('Error loading urine results:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUrineResults();
  }, []);

  return (
    <TestResultsContainer>
      <MainHeader />

      <TopBar>ผลตรวจปัสสาวะ</TopBar>

      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle>ผลตรวจปัสสาวะ (Urine Test)</PageTitle>
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : urineResults.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            ไม่พบข้อมูลผลตรวจปัสสาวะ
          </div>
        ) : (
          <>
            {/* Status Card */}
            <StatusCard $bgColor={statusColor}>
              <UrineIcon src="./ผลการตรวจปัสสาวะสี.png" alt="ผลตรวจปัสสาวะ" />
              <StatusInfo>
                <StatusTitle style={{ color: 'white' }}>ผลตรวจล่าสุด</StatusTitle>
              </StatusInfo>
            </StatusCard>

            {urineResults.map((result, index) => (
            <ResultCard key={index}>
              <CardHeader>
                <HeaderText>ผลตรวจปัสสาวะ</HeaderText>
              </CardHeader>

              <CardContent>
                <InfoRow>
                  <InfoLabel bold>วันที่ตรวจ</InfoLabel>
                  <InfoValue>{result.date}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel bold>ตรวจที่</InfoLabel>
                  <InfoValue>{result.hospital}</InfoValue>
                </InfoRow>

                <Divider />

                <SectionTitle>รายการตรวจ</SectionTitle>

                {Object.entries(result.results).map(([label, value], idx) => (
                  <InfoRow key={idx}>
                    <InfoLabel>{label}</InfoLabel>
                    <InfoValue>{value}</InfoValue>
                  </InfoRow>
                ))}
              </CardContent>
            </ResultCard>
          ))}
          </>
        )}
      </Content>
    </TestResultsContainer>
  );
};

export default UrineTestResultsPage;
